import { useState, useCallback, useMemo, useEffect } from 'react';
import { apiClient } from '@/services/apiClientSDK';
import { extractErrors } from '@/shared/utils';
import { useTranslation } from 'react-i18next';

interface UseApiClientSDKState<T = unknown> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiClientSDKOptions {
  manual?: boolean;
  onSuccess?: (data: unknown) => void;
  onError?: (error: string) => void;
}

type UseApiClientSDKResult<T = unknown> = [
  UseApiClientSDKState<T> & { apiClient: typeof apiClient },
  (apiFunction?: () => Promise<T>) => Promise<T | null>
];

export const useApiClientSDK = <T = unknown>(
  options: UseApiClientSDKOptions = {},
  initialApiFunction?: () => Promise<T>
): UseApiClientSDKResult<T> => {
  const { manual = false, onSuccess, onError } = options;
  const { t } = useTranslation();

  const [state, setState] = useState<UseApiClientSDKState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const apiClientInstance = useMemo(() => {
    return apiClient;
  }, []);

  const execute = useCallback(
    async (apiFunction?: () => Promise<T>): Promise<T | null> => {
      const functionToExecute = apiFunction || initialApiFunction;

      if (!functionToExecute) {
        console.warn('No API function provided to execute');
        return null;
      }

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        const result = await functionToExecute();

        setState((prev) => ({
          ...prev,
          data: result,
          loading: false,
          error: null,
        }));

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } catch (error: any) {
        let errorMessage: string = t('form.error');

        if (error?.response?.data?.properties) {
          errorMessage = extractErrors(error.response);
        } else if (error?.response?.data?.message) {
          errorMessage = error?.response?.data?.message;
        }

        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));

        if (onError) {
          onError(errorMessage);
        }

        return null;
      }
    },
    [initialApiFunction, onSuccess, onError]
  );

  useEffect(() => {
    if (!manual && initialApiFunction) {
      execute();
    }
  }, [manual, initialApiFunction, execute]);

  const resultState = useMemo(
    () => ({
      ...state,
      apiClient: apiClientInstance,
    }),
    [state, apiClientInstance]
  );

  return [resultState, execute];
};
